import { useEffect, useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { Term } from "@/admin/types";
import {
  fetchTermByIdRemote,
  fetchTermsRemote,
  type TermsListPagination,
} from "@/lib/termsApi";
import {
  normalizeForCompare,
  textIncludesNormalized,
  wordFirstLetter,
} from "@/lib/normalizeText";
import {
  TERMS_PAGE_SIZES,
  TERMS_PAGE_SIZE_OPTIONS,
  type TermsPageSizeOption,
} from "@/lib/termsPageSizes";

export type { Term, TermsListPagination, TermsPageSizeOption };
export { TERMS_PAGE_SIZE_OPTIONS, TERMS_PAGE_SIZES };
export { wordFirstLetter } from "@/lib/normalizeText";

function compareTermsByWord(a: Term, b: Term): number {
  return normalizeForCompare(a.word).localeCompare(
    normalizeForCompare(b.word),
    undefined,
    { sensitivity: "base", numeric: true }
  );
}

/** Portal visibility / featured rules applied after a paginated GET. */
export function filterPortalTerms(
  rows: Term[],
  options?: {
    status?: string;
    preferFeatured?: boolean;
    featuredOnly?: boolean;
  }
): Term[] {
  let list = [...rows];
  const status = (
    options?.status === "pending" || options?.status === "rejected"
      ? options.status
      : "approved"
  ) as Term["status"];
  list = list.filter(t => t.status === status);

  if (status === "approved") {
    list = list.filter(t => t.is_enabled);
  }

  if (options?.featuredOnly) {
    list = list.filter(t => t.featured);
  }

  if (options?.preferFeatured) {
    list.sort((a, b) => {
      const fa = a.featured ? 1 : 0;
      const fb = b.featured ? 1 : 0;
      if (fb !== fa) {
        return fb - fa;
      }
      return compareTermsByWord(a, b);
    });
  } else {
    list.sort(compareTermsByWord);
  }

  return list;
}

/** First character of `word` (API has no letter filter; diacritics ignored). */
export function termMatchesLetter(term: Term, letter: string): boolean {
  const L = letter.trim().toUpperCase().slice(0, 1);
  if (!L) {
    return true;
  }
  return wordFirstLetter(term.word) === L;
}

export function filterTermsByLetter(terms: Term[], letter?: string): Term[] {
  const L = letter?.trim().toUpperCase().slice(0, 1);
  if (!L) {
    return terms;
  }
  return terms.filter(t => termMatchesLetter(t, L));
}

/** Accent-insensitive match on word + meanings. Exact word match sorts first. */
export function filterTermsBySearch(terms: Term[], search?: string): Term[] {
  const q = normalizeForCompare(search ?? "");
  if (!q) {
    return terms;
  }
  const matched = terms.filter(
    t =>
      textIncludesNormalized(t.word, q) ||
      textIncludesNormalized(t.word_gujarati, q) ||
      textIncludesNormalized(t.word_devanagari, q) ||
      textIncludesNormalized(t.word_hindi, q) ||
      textIncludesNormalized(t.meaning_english, q) ||
      textIncludesNormalized(t.meaning_hindi, q) ||
      textIncludesNormalized(t.meaning_gujarati, q)
  );
  // Exact word match always comes first so the dictionary entry is immediately visible.
  return matched.sort((a, b) => {
    const aExact = normalizeForCompare(a.word) === q ? 0 : 1;
    const bExact = normalizeForCompare(b.word) === q ? 0 : 1;
    return aExact - bExact;
  });
}

export type UseTermsOptions = {
  page?: number;
  page_size?: number;
  letter?: string;
  search?: string;
  status?: string;
  preferFeatured?: boolean;
  featuredOnly?: boolean;
  limit?: number;
};

export type TermsPageResult = {
  terms: Term[];
  pagination: TermsListPagination | null;
};

function portalListRemoteOptions(
  options: UseTermsOptions
): Parameters<typeof fetchTermsRemote>[1] {
  return {
    search: options.search,
    portalAllStatuses:
      options.status === "pending" || options.status === "rejected",
    status:
      options.status === "pending" || options.status === "rejected"
        ? options.status === "rejected"
          ? "draft"
          : options.status
        : undefined,
    is_featured: options.featuredOnly ? true : undefined,
    fetchAllPages: options.featuredOnly ? true : undefined,
  };
}

/**
 * Full approved portal catalog (API has no `letter` param, so we filter client-side).
 * Streams partial (filtered + sorted) rows via `onProgress` so the UI can render
 * progressively instead of waiting for every page.
 */
export async function fetchPortalTermsCatalog(
  options?: UseTermsOptions,
  onProgress?: (partial: { terms: Term[]; total: number | null }) => void
): Promise<Term[]> {
  const filterOpts = {
    status: options?.status,
    preferFeatured: options?.preferFeatured,
    featuredOnly: options?.featuredOnly,
  };
  const { terms: rows, error } = await fetchTermsRemote("portal", {
    ...portalListRemoteOptions(options ?? {}),
    // Never forward `search` to the API — the catalog must be the complete dataset.
    // Hindi/Gujarati search terms would get no server results since the API only
    // indexes the Latin `word` field. All filtering is done client-side below.
    search: undefined,
    fetchAllPages: true,
    page_size: TERMS_PAGE_SIZES.fetchAllChunk,
    onProgress: onProgress
      ? p =>
          onProgress({
            terms: filterPortalTerms(p.terms, filterOpts),
            total: p.total,
          })
      : undefined,
  });

  if (error) {
    throw new Error(error);
  }

  return filterPortalTerms(rows ?? [], filterOpts);
}

function paginateTermsClient(
  terms: Term[],
  page: number,
  page_size: number
): TermsPageResult {
  const totalCount = terms.length;
  const start = (page - 1) * page_size;
  return {
    terms: terms.slice(start, start + page_size),
    pagination: {
      count: totalCount,
      page,
      page_size,
    },
  };
}

/** Derive a letter/search/page view from the (possibly partial) catalog — pure, client-side. */
function derivePortalPage(
  all: Term[],
  options?: UseTermsOptions
): TermsPageResult {
  const page = Math.max(1, options?.page ?? 1);
  const page_size = options?.page_size ?? TERMS_PAGE_SIZES.browseDefault;
  const letter = options?.letter?.trim().toUpperCase().slice(0, 1);

  let terms = letter ? filterTermsByLetter(all, letter) : all;
  terms = filterTermsBySearch(terms, options?.search);

  if (options?.limit != null && options.limit > 0) {
    terms = terms.slice(0, options.limit);
    return { terms, pagination: { count: terms.length, page: 1, page_size } };
  }
  if (options?.featuredOnly) {
    return {
      terms,
      pagination: {
        count: terms.length,
        page: 1,
        page_size: terms.length || page_size,
      },
    };
  }
  return paginateTermsClient(terms, page, page_size);
}

/** Catalog snapshot that grows as pages stream in. */
export type PortalCatalog = {
  terms: Term[];
  total: number | null;
  complete: boolean;
};

const EMPTY_CATALOG: PortalCatalog = { terms: [], total: null, complete: false };
const EMPTY_TERMS: Term[] = [];

export type TermsLoadProgress = {
  /** Words available to display so far. */
  loaded: number;
  /** Server-reported total (null until known). */
  total: number | null;
  /** True once every page has loaded. */
  complete: boolean;
};

function catalogQueryKey(options?: UseTermsOptions) {
  return [
    "terms",
    "portal-api",
    "catalog",
    options?.status ?? "approved",
    options?.preferFeatured ?? false,
    options?.featuredOnly ?? false,
  ] as const;
}

/**
 * Invalidate every cached portal term read (catalog, count, and single-term
 * details). Call after a mutation that changes the public dataset — approve,
 * reject, edit, add, delete — so the public site reflects it immediately:
 * active queries refetch now, inactive ones refetch on their next mount.
 */
export function invalidatePortalTermsCache(queryClient: QueryClient): void {
  // Catalog + count both live under ["terms", "portal-api", ...].
  void queryClient.invalidateQueries({ queryKey: ["terms", "portal-api"] });
  // Single-term details live under ["term", "portal-api", id].
  void queryClient.invalidateQueries({ queryKey: ["term", "portal-api"] });
}

/** React Query options for the streaming catalog (shared by the hook and prefetch). */
function portalCatalogQueryOptions(
  queryClient: QueryClient,
  options?: UseTermsOptions
) {
  const queryKey = catalogQueryKey(options);
  return {
    queryKey,
    queryFn: async (): Promise<PortalCatalog> => {
      let total: number | null = null;
      // Throttle progressive commits: pushing every page (~58) into the cache
      // re-renders + re-sorts the whole catalog each time and can block
      // navigation. Flush the latest partial at most every 500ms instead.
      let latest: Term[] | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const flush = () => {
        timer = null;
        if (latest) {
          queryClient.setQueryData<PortalCatalog>(queryKey, {
            terms: latest,
            total,
            complete: false,
          });
          latest = null;
        }
      };
      const finalTerms = await fetchPortalTermsCatalog(options, partial => {
        total = partial.total ?? total;
        latest = partial.terms;
        if (timer == null) {
          timer = setTimeout(flush, 500);
        }
      });
      if (timer != null) {
        clearTimeout(timer);
      }
      return { terms: finalTerms, total, complete: true };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Re-fetch on every mount (each tab navigation remounts the page via
    // RoutePage) so switching Home/Browse/Contribute/Search always pulls the
    // latest catalog. Cached rows render instantly while the refresh streams in
    // the background — no manual reload needed.
    refetchOnMount: "always" as const,
    retry: 2,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 10000),
  };
}

/**
 * Streaming portal catalog. `data.terms` grows as each page arrives (pushed into
 * the React Query cache via `setQueryData`), so subscribers render progressively.
 * Results stay cached/shared across pages by query key.
 */
function usePortalCatalog(options?: UseTermsOptions, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery<PortalCatalog>({
    ...portalCatalogQueryOptions(queryClient, options),
    enabled,
  });
}

/**
 * Warm the shared portal catalog as early as possible (e.g. from the site
 * layout) so it's ready — or already streaming — before the user opens Browse.
 * Prefetch only populates the cache; it doesn't subscribe, so callers don't
 * re-render as pages stream in.
 */
export function usePrefetchPortalCatalog(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    void queryClient.prefetchQuery(portalCatalogQueryOptions(queryClient));
  }, [queryClient]);
}

export type UseTermsResult = {
  data: Term[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  progress: TermsLoadProgress;
};

export function useTerms(options?: UseTermsOptions): UseTermsResult {
  const catalog = usePortalCatalog(options);
  const data = catalog.data ?? EMPTY_CATALOG;
  const terms = useMemo(
    () => derivePortalPage(data.terms, options).terms,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data.terms,
      options?.letter,
      options?.search,
      options?.page,
      options?.page_size,
      options?.limit,
      options?.featuredOnly,
    ]
  );
  return {
    data: terms,
    isLoading: catalog.isPending,
    isFetching: catalog.isFetching,
    error: catalog.error,
    refetch: catalog.refetch,
    progress: {
      loaded: data.terms.length,
      total: data.total,
      complete: data.complete,
    },
  };
}

export type UseTermsPageResult = {
  data: TermsPageResult;
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: unknown;
  refetch: () => void;
  progress: TermsLoadProgress;
};

/** Paginated portal list; renders progressively and always sorted as the catalog streams in. */
export function useTermsPage(
  options: UseTermsOptions & { page: number; enabled?: boolean }
): UseTermsPageResult {
  const enabled = options.enabled !== false;
  const catalog = usePortalCatalog(options, enabled);
  const data = catalog.data ?? EMPTY_CATALOG;
  const pageResult = useMemo(
    () => derivePortalPage(data.terms, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data.terms,
      options.page,
      options.page_size,
      options.letter,
      options.search,
      options.limit,
      options.featuredOnly,
    ]
  );
  return {
    data: pageResult,
    isLoading: catalog.isPending,
    isPending: catalog.isPending,
    isFetching: catalog.isFetching,
    isPlaceholderData: false,
    error: catalog.error,
    refetch: catalog.refetch,
    progress: {
      loaded: data.terms.length,
      total: data.total,
      complete: data.complete,
    },
  };
}

/** Autocomplete suggestions — reuses the shared streaming catalog. */
export function useTermSearchSuggestions(search: string, enabled = true) {
  const q = search.trim();
  const catalog = usePortalCatalog(undefined, enabled && q.length >= 2);
  const rows = catalog.data?.terms ?? EMPTY_TERMS;
  const data = useMemo(
    () =>
      q
        ? filterTermsBySearch(rows, q).slice(0, TERMS_PAGE_SIZES.autocomplete)
        : EMPTY_TERMS,
    [rows, q]
  );
  return { data, isLoading: catalog.isPending };
}

/**
 * Resolve curated related-term UUIDs to full terms, preserving order.
 * Reuses the cached portal catalog, so any number of related terms is a single
 * in-memory lookup — no extra requests.
 */
export function usePortalTermsByIds(ids: string[]) {
  const catalog = usePortalCatalog(undefined, ids.length > 0);
  const rows = catalog.data?.terms ?? EMPTY_TERMS;
  const idsKey = ids.join(",");
  const data = useMemo(
    () => {
      const byId = new Map(rows.map(t => [t.id, t]));
      return ids
        .map(id => byId.get(id))
        .filter((t): t is Term => Boolean(t));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, idsKey]
  );
  return { data };
}

export function useTerm(id: string) {
  return useQuery({
    queryKey: ["term", "portal-api", id],
    queryFn: async (): Promise<Term> => {
      const { term, error } = await fetchTermByIdRemote(id, "portal");
      if (error) {
        throw new Error(error);
      }
      if (!term || (!term.id && !term.word)) {
        throw new Error("Term not found");
      }
      if (term.status !== "approved" || !term.is_enabled) {
        throw new Error("Term not found");
      }
      return term;
    },
    enabled: !!id.trim(),
    staleTime: 5 * 60 * 1000,
    // Re-fetch each time a term page is opened (it remounts via RoutePage) so
    // edits to a meaning/image/audio show without a manual reload. Cheap — a
    // single term, not the whole catalog.
    refetchOnMount: "always",
    retry: 2,
  });
}

export function useTermsCount() {
  return useQuery({
    queryKey: ["terms", "portal-api", "count"],
    queryFn: async (): Promise<number> => {
      const { pagination, error } = await fetchTermsRemote("portal", {
        page: 1,
        page_size: TERMS_PAGE_SIZES.countProbe,
      });
      if (error) {
        throw new Error(error);
      }
      return pagination?.count ?? 0;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}
